import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { SectionHead } from '@/components/redesign/bits'
import { NativeSelect } from '@/components/ui/native-select'
import { SearchField } from '@/components/ui/search-field'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState, ErrorState } from '@/components/ui/states'
import { cn } from '@/lib/cn'
import { formatDateTime, numberFmt } from '@/lib/format'
import { type Campaign, type MarketingSend, type MarketingSendFilter, useMarketingSends } from './api'
import { NOT_TRACKED } from './format'

const SEND = {
  sent: { label: 'Sent', tone: 'ok' },
  queued: { label: 'Waiting', tone: 'warn' },
  skipped: { label: 'Skipped', tone: '' },
  failed: { label: 'Failed', tone: 'bad' },
} as const satisfies Record<MarketingSend['status'], { label: string; tone: string }>

const statuses: { value: MarketingSendFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'sent', label: 'Sent' },
  { value: 'queued', label: 'Waiting' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'failed', label: 'Failed' },
]

/**
 * Every copy of every marketing email, like Announcements' sends: who it went to, whether it was
 * sent, skipped (and why) or failed, and what they did with it (a service notice's copies say
 * opens and clicks aren't tracked). With `campaignId`, only that email's copies.
 */
export function MarketingSends({
  campaigns = [],
  campaignId,
}: {
  campaigns?: Campaign[]
  campaignId?: string
}) {
  const [params, setParams] = useSearchParams()
  const update = (next: Record<string, string>, resetPage = false) =>
    setParams(
      (p) => {
        const merged = new URLSearchParams(p)
        for (const [k, v] of Object.entries(next)) {
          if (v === '') merged.delete(k)
          else merged.set(k, v)
        }
        if (resetPage) merged.delete('page')
        return merged
      },
      { replace: true },
    )
  const status = (params.get('sends') as MarketingSendFilter | null) ?? 'all'
  const campaign = campaignId ?? params.get('campaign') ?? ''
  const to = params.get('to') ?? ''
  const page = Number(params.get('page') ?? 1)
  const sends = useMarketingSends(status, campaign, to, page)
  const filtering = status !== 'all' || (campaignId === undefined && campaign !== '') || to !== ''

  const [draft, setDraft] = useState(to)
  useEffect(() => {
    const t = setTimeout(() => {
      if (draft.trim() !== to) update({ to: draft.trim() }, true)
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])

  return (
    <section className="sec">
      <SectionHead
        title="Email sends"
        extra={
          <div className="bar">
            <Segmented
              label="Send status"
              value={status}
              options={statuses}
              onChange={(v) => update({ sends: v === 'all' ? '' : v }, true)}
            />
            {campaignId === undefined && (
              <NativeSelect
                aria-label="Marketing email"
                value={campaign}
                onChange={(e) => update({ campaign: e.target.value }, true)}
                className="h-9 w-52 text-[13px]"
              >
                <option value="">Every email</option>
                {campaigns
                  .filter((c) => c.status !== 'draft')
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </NativeSelect>
            )}
            <SearchField
              label="Search sends"
              placeholder="Recipient’s email"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
          </div>
        }
      />

      {sends.isPending ? (
        <Skeleton className="h-40 rounded-[14px]" />
      ) : sends.isError ? (
        <div className="card">
          <ErrorState error={sends.error} onRetry={() => void sends.refetch()} />
        </div>
      ) : sends.data.data.length === 0 ? (
        <div className="card">
          <EmptyState
            title={filtering ? 'No sends match these filters' : 'Nothing sent yet'}
            description={
              filtering
                ? 'Try another status or email, or clear the search.'
                : 'Each person’s copy appears here once a marketing email is sent.'
            }
            action={
              filtering && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setDraft('')
                    update({ sends: '', campaign: '', to: '' }, true)
                  }}
                >
                  Clear filters
                </button>
              )
            }
          />
        </div>
      ) : (
        <div className="tw">
          <table className="t" style={{ minWidth: 640 }}>
            <caption className="sr-only">Marketing email sends</caption>
            <thead>
              <tr>
                {campaignId === undefined && <th>Email</th>}
                <th>To</th>
                <th>Status</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {sends.data.data.map((s) => (
                <tr key={s.id}>
                  {campaignId === undefined && (
                    <td>
                      <b>{s.campaign ?? 'Deleted email'}</b>
                    </td>
                  )}
                  <td>{s.to ?? <span className="m">—</span>}</td>
                  <td>
                    <span className={cn('chip', SEND[s.status].tone)}>{SEND[s.status].label}</span>
                    {s.reason && <small style={{ whiteSpace: 'normal' }}>{s.reason}</small>}
                  </td>
                  <td className="m" style={{ whiteSpace: 'nowrap' }}>
                    {s.sent_at
                      ? `Sent ${formatDateTime(s.sent_at)}`
                      : s.queued_at
                        ? `Queued ${formatDateTime(s.queued_at)}`
                        : '—'}
                    {!s.counts_opens ? (
                      s.status === 'sent' && <small title={NOT_TRACKED}>Not tracked (service notice)</small>
                    ) : s.clicked_at ? (
                      <small>Clicked {formatDateTime(s.clicked_at)}</small>
                    ) : (
                      s.opened_at && <small>Opened {formatDateTime(s.opened_at)}</small>
                    )}
                    {s.unsubscribed_at && <small>Unsubscribed {formatDateTime(s.unsubscribed_at)}</small>}
                    {s.bounced_at && <small>Bounced {formatDateTime(s.bounced_at)}</small>}
                    {s.complained_at && <small>Marked as spam {formatDateTime(s.complained_at)}</small>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="t-foot">
            <span>
              {sends.data.meta.last_page > 1
                ? `Page ${sends.data.meta.current_page} of ${sends.data.meta.last_page} · `
                : ''}
              {numberFmt.format(sends.data.meta.total)} {sends.data.meta.total === 1 ? 'send' : 'sends'}
            </span>
            {sends.data.meta.last_page > 1 && (
              <span className="acts">
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={page <= 1}
                  onClick={() => update({ page: String(page - 1) })}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={page >= sends.data.meta.last_page}
                  onClick={() => update({ page: String(page + 1) })}
                >
                  Next
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
